import { sanitizeHtml } from './sanitization';

/**
 * Clean HTML template content for email sending
 * Removes editor-specific styling (like variable highlighting) while preserving formatting
 */
export const cleanTemplateForEmail = (html: string): string => {
  if (!html) return '';
  
  return html
    // Remove all class attributes (Quill-specific classes)
    .replace(/\s*class=\"[^\"]*\"/g, '')
    
    // Remove variable highlighting styles more aggressively
    .replace(/style=\"[^\"]*\"/g, (match) => {
      let cleaned = match
        // Remove background colors (variable highlighting)
        .replace(/background[^;]*;?/g, '')
        .replace(/background-color[^;]*;?/g, '')
        
        // Remove specific blue colors used for variables
        .replace(/color:\s*#1e40af[^;]*;?/g, '')
        .replace(/color:\s*#dbeafe[^;]*;?/g, '')
        .replace(/color:\s*rgb\(30,\s*64,\s*175\)[^;]*;?/g, '')
        .replace(/color:\s*rgb\(219,\s*234,\s*254\)[^;]*;?/g, '')
        
        // Clean up multiple semicolons and whitespace
        .replace(/;;+/g, ';')
        .replace(/;\s*;/g, ';')
        .replace(/^\s*style=\"\s*;+\s*/, 'style="')
        .replace(/;+\s*\"$/, '"')
        .replace(/style=\"\s*\"/, '');
      
      // If style attribute is empty after cleaning, remove it entirely
      if (cleaned === 'style=""' || cleaned === 'style=";"' || cleaned.match(/style=\"\s*;*\s*\"/)) {
        return '';
      }
      
      // Ensure email-safe font-family if there are other styles
      if (cleaned && cleaned !== 'style=""' && !cleaned.includes('font-family')) {
        cleaned = cleaned.replace('style="', 'style="font-family: -apple-system, BlinkMacSystemFont, \'Segoe UI\', Roboto, sans-serif; ');
      }
      
      return cleaned;
    })
    
    // Remove any remaining empty style attributes
    .replace(/\s*style=\"\s*\"/g, '')
    .replace(/\s*style=\";\"/g, '')
    .replace(/\s*style=\"\s*;\s*\"/g, '')
    
    // Replace empty paragraphs for better email compatibility
    .replace(/<p><br><\/p>/g, '<p>&nbsp;</p>')
    .replace(/<p><\/p>/g, '<p>&nbsp;</p>')
    .replace(/<p>\s*<\/p>/g, '<p>&nbsp;</p>')
    
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    .replace(/>\s+</g, '><')
    .trim();
};

interface TemplateVariables {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
}

/**
 * Replaces template variables in a string with actual values
 * Uses global replace to handle multiple occurrences of the same variable
 */
export const replaceTemplateVariables = (
  template: string,
  variables: TemplateVariables
): string => {
  return template
    .replace(/\{\{firstName\}\}/g, variables.firstName)
    .replace(/\{\{lastName\}\}/g, variables.lastName)
    .replace(/\{\{email\}\}/g, variables.email)
    .replace(/\{\{company\}\}/g, variables.company);
};

/**
 * Checks if a template contains specific variables
 */
export const templateContainsVariable = (template: string, variableName: string): boolean => {
  return template.includes(`{{${variableName}}}`);
};

/**
 * Gets all variables used in a template
 */
export const getTemplateVariables = (template: string): string[] => {
  const matches = template.match(/\{\{(\w+)\}\}/g) || [];
  return matches.map(match => match.replace(/[{}]/g, ''));
};

/**
 * Format email body HTML for preview display
 * Sanitizes HTML to prevent XSS, then applies styling for paragraphs and variable pills
 */
export const formatEmailBodyForPreview = (html: string): string => {
  if (!html) return '';

  // Sanitize HTML first to prevent XSS
  const sanitized = sanitizeHtml(html);

  // Apply styling for preview display
  return sanitized
    // Add margin-bottom to all <p> tags, preserving existing attributes
    .replace(/<p(\s[^>]*)?\s*>/g, (_match, attrs) => {
      if (!attrs) {
        return '<p style="margin-bottom: 1em;">';
      }
      // If tag already has a style attribute, append to it
      if (/style\s*=/.test(attrs)) {
        return `<p${attrs.replace(/style\s*=\s*"([^"]*)"/, 'style="$1; margin-bottom: 1em;"')}>`;
      }
      // Otherwise add new style attribute
      return `<p${attrs} style="margin-bottom: 1em;">`;
    })
    .replace(/<br\s*\/?>/g, '<br style="display: block; margin: 0.5em 0;">')
    .replace(/<\/p>\s*<p/g, '</p><p')
    // Convert {{variable}} to styled pills
    .replace(/\{\{([^}]+)\}\}/g, '<span class="variable-pill">{{$1}}</span>');
};

export type { TemplateVariables };

